import { useParams } from 'react-router';
import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDate } from '@castfeeds/i18n';
import { useT } from '@/i18n';
import { ErrorBox, Field, Ltr, PoweredBy, Spinner, StatusPill, Wordmark } from '@/components/ui';
import { isTenantBrand, useBranding } from '@/lib/branding';
import { LanguageSwitch } from '@/layout/AppShell';
import { api } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { qk } from '@/lib/api/keys';
import { clipDuration, mmss } from '@/lib/format';
import { logInfo } from '@/lib/log';
import { STAGE_PILL } from '@/lib/constants';
import { textOn } from '@/lib/color';
import type { PublicReview, ReviewItem } from '@/lib/api/types';

/** No-login client review page (/r/:token). Watermarked, no download; customers comment and approve or request changes (plan §17). */
export function ReviewPage() {
  const { token = '' } = useParams();
  const { t, locale } = useT();
  const q = useQuery({ queryKey: qk.review(token), queryFn: () => api.review.open(token), retry: false });
  const tenant = isTenantBrand(useBranding((s) => s.branding));

  let body: React.ReactNode;
  if (q.isLoading) body = <Spinner label={t('loading')} />;
  else if (q.error) {
    const status = q.error instanceof ApiError ? q.error.status : 0;
    body =
      status === 410 || status === 404 ? (
        <div className="card card-pad" style={{ padding: 28 }}>
          <h1 className="h1">{status === 410 ? t('reviewGone') : t('reviewNotFound')}</h1>
          <p className="muted" style={{ margin: 0 }}>{t('reviewGoneSub')}</p>
        </div>
      ) : (
        <ErrorBox error={q.error} onRetry={() => q.refetch()} />
      );
  } else if (q.data) body = <ReviewBody data={q.data} locale={locale} token={token} />;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <header className="shell-header" style={{ height: 52 }}>
        <Wordmark size={16} />
        {/* Branded review (WS J): the tenant's mark, and "Powered by Castfeeds" only while the tenant keeps it. */}
        {tenant ? <PoweredBy className="muted small push" /> : <span className="muted small push">{t('poweredBy')}</span>}
        <LanguageSwitch />
      </header>
      <main className="page" style={{ maxWidth: 1100, margin: '0 auto' }}>{body}</main>
    </div>
  );
}

const NAME_KEY = 'cf.reviewer-name';
const loadName = () => {
  try {
    return localStorage.getItem(NAME_KEY) ?? '';
  } catch {
    return '';
  }
};

function ReviewBody({ data, locale, token }: { data: PublicReview; locale: 'en' | 'he'; token: string }) {
  const { t } = useT();
  const primary = data.customer.colours?.primary ?? '#FF4A2E';
  const [name, setName] = useState(loadName);
  const saveName = (v: string) => {
    setName(v);
    try {
      localStorage.setItem(NAME_KEY, v);
    } catch {
      /* private mode: the name is kept for this visit only */
    }
  };
  return (
    <>
      <div className="card row wrap" style={{ padding: 20, gap: 14, borderTop: `4px solid ${primary}` }}>
        <div className="stack">
          <h1 className="h1">{t('reviewTitle', { customer: data.customer.name })}</h1>
          <span className="muted">{t('reviewExpires', { date: formatDate(locale, data.expiresAt) })}</span>
        </div>
        {data.watermark ? <span className="pill push" style={{ background: primary, color: textOn(primary) }}>{t('reviewWatermark')}</span> : null}
      </div>
      {data.items.length === 0 ? (
        <div className="card"><div className="empty">{t('reviewEmpty')}</div></div>
      ) : (
        <>
          <div className="card card-pad" style={{ maxWidth: 420 }}>
            <Field label={t('reviewYourName')} hint={t('reviewYourNameHint')}>
              {(p) => <input {...p} className="input" value={name} maxLength={120} autoComplete="name" onChange={(e) => saveName(e.target.value)} />}
            </Field>
          </div>
          <div className="grid-cards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}>
            {data.items.map((it) => <ReviewCard key={it.id} item={it} token={token} watermark={data.watermark} name={name.trim()} />)}
          </div>
        </>
      )}
    </>
  );
}

/** One video on a review link: player, "what changed", comments, and the customer's decision. */
function ReviewCard({ item: it, token, watermark, name }: { item: ReviewItem; token: string; watermark: boolean; name: string }) {
  const { t } = useT();
  const qc = useQueryClient();
  const video = useRef<HTMLVideoElement | null>(null);
  const [draft, setDraft] = useState('');
  const [note, setNote] = useState('');
  const [thanks, setThanks] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: qk.review(token) });
  const at = () => (video.current ? Math.round(video.current.currentTime * 1000) : null);
  const comment = useMutation({ mutationFn: () => api.review.comment(token, it.id, { name, body: draft.trim(), t_ms: at() }), onSuccess: () => { setDraft(''); void refresh(); } });
  const decide = useMutation({
    mutationFn: (decision: 'approve' | 'request_changes') => api.review.decide(token, it.id, { name, decision, note: note.trim() || undefined, expect_version: it.version }),
    onSuccess: (r, decision) => {
      logInfo('review', `decision=${decision} item=${it.id} stage=${r.stage}`);
      setThanks(true);
      setNote('');
      void refresh();
    },
  });
  const vertical = it.format === '9:16' || it.format === '4:5';
  const open = it.stage === 'client';
  return (
    <article className="card">
      <div className="media" style={{ aspectRatio: it.format ? it.format.replace(':', '/') : '16/9', borderRadius: 0, maxHeight: vertical ? 520 : undefined }}>
        {it.url ? (
          <video ref={video} src={it.url} poster={it.poster_url ?? undefined} controls controlsList="nodownload" disablePictureInPicture onContextMenu={(e) => e.preventDefault()} style={{ width: '100%', height: '100%', background: '#121318' }} />
        ) : (
          <div className="stripes" style={{ width: '100%', height: '100%' }} />
        )}
        {watermark ? <span style={{ position: 'absolute', top: 8, left: 8, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: 'rgba(18,19,24,.75)', color: '#F6F4EF', pointerEvents: 'none' }}>{t('reviewWatermark')}</span> : null}
      </div>
      <div className="stack" style={{ padding: '10px 14px', gap: 8 }}>
        <div className="row wrap" style={{ gap: 8 }}>
          <span style={{ fontWeight: 500 }}>{it.title}</span>
          {it.version ? <Ltr className="mono muted small">v{it.version}</Ltr> : null}
          {it.stage ? <StatusPill status={STAGE_PILL[it.stage]} /> : null}
          {it.duration_ms ? <Ltr className="mono muted small push">{clipDuration(it.duration_ms)}</Ltr> : null}
        </div>
        {it.project ? <span className="muted small">{it.project}</span> : null}
        {it.note ? <span className="small"><span className="label-caps">{t('versionDiff')}</span> · {it.note}</span> : null}
        {it.comments?.length ? (
          <ul role="list" className="stack" style={{ listStyle: 'none', margin: 0, padding: 0, gap: 6 }}>
            {it.comments.map((c) => (
              <li key={c.id} className="small" style={{ opacity: c.resolved ? 0.55 : 1 }}>
                <strong>{c.author.name}</strong>
                {c.t_ms !== null ? <Ltr className="mono muted"> {mmss(Math.round(c.t_ms / 1000))}</Ltr> : null} · {c.body}
              </li>
            ))}
          </ul>
        ) : null}
        <form
          className="row"
          style={{ gap: 6 }}
          onSubmit={(e) => {
            e.preventDefault();
            if (name && draft.trim()) comment.mutate();
          }}
        >
          <label className="sr-only" htmlFor={`rc-${it.id}`}>{t('comments')}</label>
          <input id={`rc-${it.id}`} className="input" style={{ flex: 1 }} value={draft} maxLength={2000} placeholder={t('commentAt', { tc: mmss(Math.round((at() ?? 0) / 1000)) })} onChange={(e) => setDraft(e.target.value)} />
          <button type="submit" className="btn btn-sm" disabled={!name || !draft.trim() || comment.isPending}>{t('post')}</button>
        </form>
        {open ? (
          <div className="stack" style={{ gap: 6 }}>
            <label className="sr-only" htmlFor={`rn-${it.id}`}>{t('reqChangesNote')}</label>
            <input id={`rn-${it.id}`} className="input" value={note} maxLength={1000} placeholder={t('reqChangesNote')} onChange={(e) => setNote(e.target.value)} />
            <div className="row" style={{ gap: 8 }}>
              <button type="button" className="btn" disabled={!name || decide.isPending} onClick={() => decide.mutate('request_changes')}>{t('reqChanges')}</button>
              <button type="button" className="btn btn-primary push" disabled={!name || decide.isPending} onClick={() => decide.mutate('approve')}>{t('approve')}</button>
            </div>
            {it.required_customer_approvals && it.required_customer_approvals > 1 ? <span className="muted small">{t('apNeeds', { n: it.customer_approvals ?? 0, required: it.required_customer_approvals })}</span> : null}
          </div>
        ) : it.stage === 'approved' ? (
          <span className="small" style={{ color: 'var(--green)' }}>{t('reviewApproved')}</span>
        ) : it.stage === 'changes' || it.stage === 'review' ? (
          <span className="muted small">{t('reviewWithStudio')}</span>
        ) : null}
        {thanks ? <span role="status" className="muted small">{t('reviewThanks')}</span> : null}
        {comment.error ? <ErrorBox error={comment.error} /> : null}
        {decide.error ? <ErrorBox error={decide.error} /> : null}
      </div>
    </article>
  );
}
